import { toast } from 'sonner';
import notificationService from '../services/notificationService';
import { mapCasePriorityToBatchPriority } from '../lib/casePriority';
import { getOutcomeValidationToast } from '../lib/milestoneOutcomeUtils';
import { fetchConceptionPhaseTotalAligners } from '../services/caseProductionPhasesService';
import { completeCaseShippingPhase } from '../lib/caseLifecycleActions';

export const PHASE_CURRENT_PHASE_MAP = {
  1: 'submission',
  2: 'review',
  3: 'design',
  4: 'pre_production',
  5: 'production',
  6: 'thermoforming',
  7: 'finishing_cut',
  8: 'quality_check',
  9: 'shipping',
};

export function generateLotBatchNumber() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `LOT-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
}

/** Remise à zéro des jalons lors d'une nouvelle passe sur une phase (après rejet / reprise). */
export async function resetPhaseMilestonesForRestart(supabase, phaseId) {
  if (!phaseId) return;
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('phase_milestones')
    .update({
      is_completed: false,
      completed_date: null,
      is_rejected: false,
      rejected_date: null,
      completion_type: null,
      outcome_quantity: null,
      validated_by_user_id: null,
      validated_at: null,
      note: null,
      updated_at: now,
    })
    .eq('phase_id', phaseId);
  if (error) console.warn('resetPhaseMilestonesForRestart:', error.message);
}

/** Reset jalons d'une phase identifiée par cas / groupe / ordre. */
export async function resetPhaseMilestonesForPhaseOrder(supabase, caseId, group, phaseOrder) {
  const { data } = await supabase
    .from('case_timeline_phases')
    .select('id')
    .eq('case_id', caseId)
    .eq('group', String(group))
    .eq('phase_order', phaseOrder)
    .maybeSingle();
  if (data?.id) await resetPhaseMilestonesForRestart(supabase, data.id);
}

/**
 * Met à jour le statut d'une phase timeline et enchaîne les effets de bord (lots, cas, notifications).
 * Ne gère pas d'état React — envelopper avec setUpdatingPhaseId côté UI si besoin.
 */
export async function applyPhaseStatusChange(supabase, ctx) {
  const {
    caseId,
    caseData,
    phaseId,
    newStatus,
    phaseOrder,
    group,
    generateBatchNumber = generateLotBatchNumber,
    onPhaseUpdated,
  } = ctx;

  const now = new Date().toISOString();
  const g = String(group);
  const gNum = parseInt(g, 10);
  const isShippingCompletion =
    newStatus === 'completed' &&
    ((g === '1' && phaseOrder === 9) || (gNum >= 2 && phaseOrder === 6));

  if (isShippingCompletion) {
    const result = await completeCaseShippingPhase(supabase, phaseId);
    if (!result?.ok) throw new Error(result?.error || 'shipping_completion_failed');

    toast.success(
      gNum >= 2
        ? `🎉 Lot ${gNum} expédié! Commande marquée comme terminée.`
        : '🎉 Commande livrée et terminée.'
    );
    if (caseData?.dentistId && !result?.already_completed) {
      await notificationService.createNotification({
        userId: caseData.dentistId,
        type: 'success',
        title: 'Commande livrée',
        message: `La commande ${caseData?.caseNumber || caseId} a été livrée`,
        link: `/case-detail/${caseId}`,
        metadata: {
          caseId,
          caseNumber: caseData?.caseNumber,
          event: 'case_delivered',
          lot: gNum,
        },
      });
    }
    if (onPhaseUpdated) onPhaseUpdated();
    return;
  }

  const { data: phaseBefore } = await supabase
    .from('case_timeline_phases')
    .select('status')
    .eq('id', phaseId)
    .maybeSingle();
  const previousStatus = phaseBefore?.status ?? null;

  const updates = { status: newStatus, updated_at: now };
  if (newStatus === 'completed') {
    updates.end_date = now;
    updates.progress_percentage = 100;
  }

  const { error } = await supabase.from('case_timeline_phases').update(updates).eq('id', phaseId);
  if (error) throw error;

  if (
    newStatus === 'in_progress' &&
    (previousStatus === 'pending' || previousStatus === 'cancelled')
  ) {
    await resetPhaseMilestonesForRestart(supabase, phaseId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GROUP 1 — Initial order workflow
  // ─────────────────────────────────────────────────────────────────────────
  if (g === '1') {
    if (phaseOrder === 2) {
      if (newStatus === 'completed') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'completed', end_date: now, progress_percentage: 100, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 1);
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', start_date: now, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 3);
        await resetPhaseMilestonesForPhaseOrder(supabase, caseId, '1', 3);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: 'design', updated_at: now })
          .eq('id', caseId);
        toast.success('Révision terminée! Phase de conception démarrée.');
        if (caseData?.dentistId) {
          await notificationService.notifyCaseStatusChange(
            caseData.dentistId,
            caseData?.caseNumber || caseId,
            'review',
            'design',
            caseId
          );
        }
      } else if (newStatus === 'cancelled') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 1);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: 'submission', updated_at: now })
          .eq('id', caseId);
        toast('Révision annulée. Retour à la phase de soumission.');
        if (caseData?.dentistId) {
          await notificationService.createNotification({
            userId: caseData.dentistId,
            type: 'warning',
            title: 'Révision rejetée',
            message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté et retourné à la phase de soumission`,
            link: `/case-detail/${caseId}`,
          });
        }
        await notificationService.notifyAllAdministrators({
          type: 'warning',
          title: 'Révision rejetée',
          message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté`,
          link: `/case-detail/${caseId}`,
        });
      }
    } else if (phaseOrder === 3) {
      if (newStatus === 'completed') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', start_date: now, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 4);
        await resetPhaseMilestonesForPhaseOrder(supabase, caseId, '1', 4);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: 'pre_production', updated_at: now })
          .eq('id', caseId);
        const deadlineDate = caseData?.deadline
          ? new Date(caseData.deadline).toISOString()
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        const { data: existingBatch } = await supabase
          .from('production_batches')
          .select('id')
          .eq('case_id', caseId)
          .eq('current_phase', '1')
          .maybeSingle();

        if (!existingBatch) {
          const conceptionAligners = await fetchConceptionPhaseTotalAligners(caseId);
          const half =
            conceptionAligners != null && !Number.isNaN(Number(conceptionAligners))
              ? Math.floor(Number(conceptionAligners) / 2)
              : null;
          const { error: batchError } = await supabase.from('production_batches').insert({
            batch_number: generateBatchNumber(),
            patient_name: caseData?.patientName || caseData?.patient_name || 'N/A',
            clinic_name: caseData?.clinicName || caseData?.clinic_name || 'N/A',
            status: 'draft',
            priority: mapCasePriorityToBatchPriority(caseData?.priority),
            machine_id: caseData?.machineId || caseData?.machine_id || null,
            progress: 0,
            start_date: now,
            deadline: deadlineDate,
            case_id: caseId,
            current_phase: '1',
            ...(half != null
              ? {
                  model_up_from: 0,
                  model_up_to: half,
                  model_low_from: 0,
                  model_low_to: half,
                }
              : {}),
            notes: `Cas #${caseId} — Conception validée le ${new Date(now).toLocaleString('fr-FR')}`,
          });
          if (batchError) toast.warning('Phase validée, mais erreur lot de production: ' + batchError.message);
          else toast.success('Conception terminée!');
          if (caseData?.dentistId) {
            await notificationService.notifyCaseStatusChange(
              caseData.dentistId,
              caseData?.caseNumber || caseId,
              'design',
              'production',
              caseId
            );
          }
         
        }
      } else if (newStatus === 'cancelled') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'pending', start_date: null, end_date: null, progress_percentage: 0, updated_at: now })
          .eq('id', phaseId);
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 2);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: 'review', updated_at: now })
          .eq('id', caseId);
        toast('Conception rejetée. Retour à la révision initiale.');
        if (caseData?.dentistId) {
          await notificationService.createNotification({
            userId: caseData.dentistId,
            type: 'warning',
            title: 'Conception rejetée',
            message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté et retourné à la révision`,
            link: `/case-detail/${caseId}`,
          });
        }
        await notificationService.notifyAllAdministrators({
          type: 'warning',
          title: 'Conception rejetée',
          message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté`,
          link: `/case-detail/${caseId}`,
        });
      }
    } else if (phaseOrder >= 4 && phaseOrder <= 8) {
      if (newStatus === 'completed') {
        const nextOrder = phaseOrder + 1;
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', start_date: now, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', nextOrder);
        await resetPhaseMilestonesForPhaseOrder(supabase, caseId, '1', nextOrder);
        await supabase
          .from('cases')
          .update({
            status: 'in_progress',
            current_phase: PHASE_CURRENT_PHASE_MAP[nextOrder] || 'in_progress',
            updated_at: now,
          })
          .eq('id', caseId);
        toast.success(
          `Phase confirmée! ${{ 5: 'Contrôle qualité', 6: 'Expédition' }[nextOrder] ?? 'phase suivante'} démarrée.`
        );
        if (phaseOrder === 4) {
  await supabase
    .from('production_batches')
    .update({ status: 'pending', updated_at: now })
    .eq('case_id', caseId)
    .eq('current_phase', '1');
     await notificationService.notifyAllAdministrators({
            type: 'success',
            title: 'Lot de production créé',
            message: `Un lot de production a été créé pour le cas ${caseData?.caseNumber || caseId}`,
            link: `/case-detail/${caseId}`,
          });
}
        if (caseData?.dentistId) {
          const phaseName = phaseOrder === 4 ? 'Production' : 'Contrôle qualité';
          await notificationService.notifyPhaseCompletion(
            caseData.dentistId,
            caseData?.caseNumber || caseId,
            phaseName,
            caseId
          );
        }
      } else if (newStatus === 'cancelled') {
        const prevOrder = phaseOrder - 1;
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'pending', start_date: null, end_date: null, progress_percentage: 0, updated_at: now })
          .eq('id', phaseId);
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', prevOrder);
        await supabase
          .from('cases')
          .update({
            status: 'in_progress',
            current_phase: PHASE_CURRENT_PHASE_MAP[prevOrder] || 'in_progress',
            updated_at: now,
          })
          .eq('id', caseId);
        toast(
          `Phase rejetée. Retour à la phase de ${{ 3: 'conception', 4: 'production' }[prevOrder] ?? 'phase précédente'}.`
        );
        if (caseData?.dentistId) {
          await notificationService.createNotification({
            userId: caseData.dentistId,
            type: 'warning',
            title: 'Phase rejetée',
            message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté et retourné à la phase précédente`,
            link: `/case-detail/${caseId}`,
          });
        }
        await notificationService.notifyAllAdministrators({
          type: 'warning',
          title: 'Phase rejetée',
          message: `Le cas ${caseData?.caseNumber || caseId} a été rejeté`,
          link: `/case-detail/${caseId}`,
        });
      }
    } else if (phaseOrder === 9) {
      if (newStatus === 'cancelled') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'pending', start_date: null, end_date: null, progress_percentage: 0, updated_at: now })
          .eq('id', phaseId);
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', '1')
          .eq('phase_order', 8);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: 'quality_check', updated_at: now })
          .eq('id', caseId);
        toast('Expédition rejetée. Retour au contrôle qualité.');
      }
    } else {
      toast('Statut de la phase mis à jour avec succès!');
    }

  // ─────────────────────────────────────────────────────────────────────────
  // GROUP >= 2 — Follow-up lot workflow (dynamic, up to 6 phases)
  // ─────────────────────────────────────────────────────────────────────────
  } else if (gNum >= 2) {
    const GROUP2_PHASE_MAP = {
      1: 'pre_production',
      2: 'production',
      3: 'thermoforming',
      4: 'finishing_cut',
      5: 'quality_check',
      6: 'shipping',
    };

    const GROUP2_PHASE_LABELS = {
      1: 'Pré-production',
      2: 'Production',
      3: 'Thermoformage',
      4: 'Découpe / Finition',
      5: 'Contrôle qualité',
      6: 'Expédition',
    };

    // Last phase of every follow-up lot
    const LAST_PHASE_ORDER = 6;

    const deadlineDate = caseData?.deadline
      ? new Date(caseData.deadline).toISOString()
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    // ── Create production batch when phase 1 starts or completes ─────────
    if (phaseOrder === 1 && (newStatus === 'in_progress' || newStatus === 'completed')) {
      const { data: existingBatch } = await supabase
        .from('production_batches')
        .select('id')
        .eq('case_id', caseId)
        .eq('current_phase', g)
        .maybeSingle();

      if (!existingBatch) {
        const conceptionAligners = await fetchConceptionPhaseTotalAligners(caseId);
        const half =
          conceptionAligners != null && !Number.isNaN(Number(conceptionAligners))
            ? Math.floor(Number(conceptionAligners) / 2)
            : null;
        const { error: batchError } = await supabase.from('production_batches').insert({
          batch_number: generateBatchNumber(),
          patient_name: caseData?.patientName || caseData?.patient_name || 'N/A',
          clinic_name: caseData?.clinicName || caseData?.clinic_name || 'N/A',
          status: 'draft',
          priority: mapCasePriorityToBatchPriority(caseData?.priority),
          machine_id: caseData?.machineId || caseData?.machine_id || null,
          progress: 0,
          start_date: now,
          deadline: deadlineDate,
          case_id: caseId,
          current_phase: g,
          ...(half != null
            ? {
                model_up_from: 0,
                model_up_to: half,
                model_low_from: 0,
                model_low_to: half,
              }
            : {}),
          notes: `Cas #${caseId} — Lot ${gNum} démarré le ${new Date(now).toLocaleString('fr-FR')}`,
        });
        if (batchError) toast.warning('Phase démarrée, mais erreur lot de production: ' + batchError.message);
        else toast.success(`Lot ${gNum} de production créé!`);
      }
    }

    // ── Intermediate phases (1 → 5): advance to next ─────────────────────
    if (phaseOrder >= 1 && phaseOrder < LAST_PHASE_ORDER) {
      if (newStatus === 'completed') {
        const nextOrder = phaseOrder + 1;
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', start_date: now, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', g)
          .eq('phase_order', nextOrder);
        await resetPhaseMilestonesForPhaseOrder(supabase, caseId, g, nextOrder);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: GROUP2_PHASE_MAP[nextOrder], updated_at: now })
          .eq('id', caseId);
        toast.success(
          `Phase confirmée! ${GROUP2_PHASE_LABELS[nextOrder] ?? 'phase suivante'} démarrée.`
        );

                if (phaseOrder === 1) {
  await supabase
    .from('production_batches')
    .update({ status: 'pending', updated_at: now })
    .eq('case_id', caseId)
    .eq('current_phase', g);
     await notificationService.notifyAllAdministrators({
            type: 'success',
            title: 'Lot de production créé',
            message: `Un lot de production a été créé pour le cas ${caseData?.caseNumber || caseId}`,
            link: `/case-detail/${caseId}`,
          });
}
      } else if (newStatus === 'cancelled') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'pending', start_date: null, end_date: null, progress_percentage: 0, updated_at: now })
          .eq('id', phaseId);
        if (phaseOrder > 1) {
          const prevOrder = phaseOrder - 1;
          await supabase
            .from('case_timeline_phases')
            .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
            .eq('case_id', caseId)
            .eq('group', g)
            .eq('phase_order', prevOrder);
          await supabase
            .from('cases')
            .update({ status: 'in_progress', current_phase: GROUP2_PHASE_MAP[prevOrder], updated_at: now })
            .eq('id', caseId);
          toast(
            `Phase rejetée. Retour à la phase "${GROUP2_PHASE_LABELS[prevOrder] ?? 'précédente'}".`
          );
        } else {
          toast('Phase rejetée. Retour au début du lot.');
        }
      }

    // ── Last phase (6 = Expédition): completion handled atomically above ──
    } else if (phaseOrder === LAST_PHASE_ORDER) {
      if (newStatus === 'cancelled') {
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'pending', start_date: null, end_date: null, progress_percentage: 0, updated_at: now })
          .eq('id', phaseId);
        const prevOrder = LAST_PHASE_ORDER - 1;
        await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', g)
          .eq('phase_order', prevOrder);
        await supabase
          .from('cases')
          .update({ status: 'in_progress', current_phase: GROUP2_PHASE_MAP[prevOrder], updated_at: now })
          .eq('id', caseId);
        toast(`Expédition rejetée. Retour à la phase "${GROUP2_PHASE_LABELS[prevOrder]}".`);
      }
    } else {
      toast('Statut de la phase mis à jour avec succès!');
    }

  } else {
    toast('Statut de la phase mis à jour avec succès!');
  }

  if (onPhaseUpdated) onPhaseUpdated();
}

/**
 * @param {function} ctx.runPhaseStatusChange — (phaseId, newStatus, phaseOrder, group) => Promise<void>
 */
export async function applyMilestoneStatusChange(supabase, ctx) {
  const {
    milestoneId,
    newStatus,
    phase,
    getGroup,
    runPhaseStatusChange,
    onPhaseUpdated,
    milestoneMeta = null,
  } = ctx;

  if (phase?.status !== 'in_progress') {
    toast.warning(
      phase?.status === 'pending'
        ? "Cette phase n'a pas encore démarré."
        : phase?.status === 'completed'
          ? 'Cette phase est déjà terminée — jalons verrouillés.'
          : phase?.status === 'cancelled'
            ? 'Cette phase a été annulée.'
            : "Cette phase n'est pas active."
    );
    return;
  }

  const now = new Date().toISOString();
  const isCompleted = newStatus === 'completed';

  const updateRow = {
    is_completed: isCompleted,
    completed_date: isCompleted ? now : null,
    updated_at: now,
  };
  if (isCompleted) {
    updateRow.is_rejected = false;
    updateRow.rejected_date = null;
  } else {
    updateRow.is_rejected = true;
    updateRow.rejected_date = now;
  }

  if (milestoneMeta) {
    if (milestoneMeta.note !== undefined) updateRow.note = milestoneMeta.note;
    if (milestoneMeta.completion_type !== undefined) {
      updateRow.completion_type = milestoneMeta.completion_type;
    }
    if (milestoneMeta.outcome_quantity !== undefined) {
      updateRow.outcome_quantity = milestoneMeta.outcome_quantity;
    }
    if (milestoneMeta.milestone_name !== undefined) {
      updateRow.milestone_name = milestoneMeta.milestone_name;
    }
    if (isCompleted && milestoneMeta.validated_by_user_id) {
      updateRow.validated_by_user_id = milestoneMeta.validated_by_user_id;
      updateRow.validated_at = milestoneMeta.validated_at || now;
    }
  }

  let { error } = await supabase.from('phase_milestones').update(updateRow).eq('id', milestoneId);

  // Repli : anciennes contraintes SQL n’autorisaient que complet|partiel|echec
  if (
    error &&
    updateRow.completion_type &&
    (updateRow.completion_type === 'laser' || updateRow.completion_type === 'manuel')
  ) {
    const { completion_type: _drop, ...withoutCutModeType } = updateRow;
    const retry = await supabase
      .from('phase_milestones')
      .update(withoutCutModeType)
      .eq('id', milestoneId);
    if (!retry.error) {
      error = null;
    } else {
      error = retry.error;
    }
  }

  if (error) {
    console.error('[applyMilestoneStatusChange]', error);
    toast.error(
      'Erreur lors de la mise à jour du jalon' +
        (error.message ? ` : ${error.message}` : '')
    );
    throw error;
  }

  const milestones = phase?.milestones ?? [];

  if (newStatus === 'completed') {
    const allNowCompleted = milestones.every((m) => (m.id === milestoneId ? true : m.completed === true));
    if (allNowCompleted && milestones.length > 0) {
      toast.success('Tous les jalons validés! Passage à la phase suivante…');
      await runPhaseStatusChange(phase.id, 'completed', phase.phase_order, getGroup(phase));
      return;
    }
    toast.success(
      milestoneMeta?.completion_type
        ? getOutcomeValidationToast(
            milestoneMeta.completion_type,
            milestoneMeta.outcome_quantity
          )
        : 'Jalon validé avec succès'
    );
  } else if (newStatus === 'rejected') {
  const allNowRejected = milestones.every((m) =>
    m.id === milestoneId ? true : m.is_rejected === true
  );
  if (allNowRejected && milestones.length > 0) {
    toast('Tous les jalons rejetés. Retour à la phase précédente…');
    await runPhaseStatusChange(phase.id, 'cancelled', phase.phase_order, getGroup(phase));
    return;
  }
  toast('Jalon rejeté');
}

  if (onPhaseUpdated) onPhaseUpdated();
}